import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {AISuggestionDisplay} from 'components/DSYS/editor/plugins/toolbar/AISuggestionDisplay';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {findHeadingNodeByHeadingId} from 'plugins/enhancedHeadings/HeadingNodeUtils';
import {useCallback, useEffect, useRef, useState} from 'react';

import {AIRule, AISuggestion} from './types';

const DEFAULT_AI_RULES: AIRule[] = [
  {
    id: 'spelling',
    name: 'Spelling',
    rule: 'Correct all spelling mistakes and unclear words. Ignore capitalization issues.',
  },
  {
    id: 'grammar',
    name: 'Grammar',
    rule: 'Improve grammar and sentence structure',
  },
];

interface AISuggestionToolbarProps {
  suggestion: AISuggestion;
  headingId: string;
  onAccept: (suggestion: AISuggestion) => void;
  onReject: (suggestion: AISuggestion) => void;
  onClose: (suggestionId?: string) => void;
}

export const AISuggestionToolbar = ({
  suggestion,
  headingId,
  onAccept,
  onReject,
  onClose,
}: AISuggestionToolbarProps) => {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [ruleDetails, setRuleDetails] = useState<AIRule | undefined>(undefined);

  // Capture the suggestion ID at mount time so it doesn't change
  const suggestionIdRef = useRef(suggestion.id);

  // Look up rule details from heading settings
  useEffect(() => {
    editor.getEditorState().read(() => {
      const node = findHeadingNodeByHeadingId(editor, headingId);

      if (node) {
        // Try to get settings from EnhancedHeadingNode if available
        const settings = (node as any).getSettings?.();

        let rule: AIRule | undefined;
        if (settings?.aiRules) {
          rule = settings.aiRules.find((r: any) => r.id === suggestion.ruleId);
        }

        if (!rule && suggestion.ruleId) {
          rule = DEFAULT_AI_RULES.find((r) => r.id === suggestion.ruleId);
        }

        if (rule) {
          setRuleDetails(rule);
        }
      }
    });
  }, [editor, headingId, suggestion.ruleId]);

  useOutsideClick(
    toolbarRef,
    () => {
      // Pass the captured suggestion ID (from mount time) so we only close if it's still the active one
      onClose(suggestionIdRef.current);
    },
    ['focused'],
    true
  );

  const handleAccept = useCallback(() => {
    analyticsLogger().logEvent('AI_SUGGESTION_ACTION', {
      type: 'accept',
      ruleId: suggestion.ruleId,
      confidence: suggestion.confidence,
    });
    onAccept(suggestion);
    onClose();
  }, [suggestion, onAccept, onClose]);

  const handleReject = useCallback(() => {
    analyticsLogger().logEvent('AI_SUGGESTION_ACTION', {
      type: 'reject',
      ruleId: suggestion.ruleId,
      confidence: suggestion.confidence,
    });
    onReject(suggestion);
    onClose();
  }, [suggestion, onReject, onClose]);

  return (
    <AISuggestionDisplay
      suggestedText={suggestion.suggestedText}
      rule={ruleDetails}
      onAccept={handleAccept}
      onReject={handleReject}
      toolbarRef={toolbarRef}
    />
  );
};
